import React, {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  clearAllHexes,
  hideCityCaptureModal,
  hideCombatResultsModal,
  hideRoundEndModal,
  loadState,
  resetState,
  setUnitAutoSelectionEnabled,
  showConstructionPhaseInfoModal,
  toggleHideModalsExceptGameEnd
} from '../../../redux/actions';
import {
  selectGameResults,
  selectGameType,
  selectGridConfigState,
  selectPlayers,
  selectHexes,
  selectSpecialBuildings,
  selectUnits
} from "../../../redux/reducers/sharedStateReducer/selectors";
import './InGameMenu.css';
import {isEnvConfEnabled} from "../../../utils/env/isEnvConfEnabled";
import {ENV_CONFIG_VARIABLES} from "../../../constants/env";
import PageButton from "../../elements/PageButton/PageButton";
import VolumeSlider from "../../VolumeSlider/VolumeSlider";
import MusicVolumeSlider from "../../MusicVolumeSlider/MusicVolumeSlider";
import FullscreenToggleButton from "../../FullscreenToggleButton/FullscreenToggleButton";
import {closeNetworkSocket, getNetworkSocket} from "../../../utils/networkSocket";
import {SCREENS} from "../../../constants/screens";
import {
  selectHideModalsExceptGameEnd,
  selectIsUnitAutoSelectionEnabled
} from "../../../redux/reducers/privateStateReducer/selectors";
import {writeUnitAutoSelectionEnabled} from "../../../utils/unitAutoSelection/unitAutoSelectionSetting";
import {GAME_TYPES} from "../../../constants/game";
import {RULES_SECTION_IDS} from "../../Modals/RulesModal/rulesSections";

const PAGE_ID = 'InGameMenu';

const InGameMenu = ({isOpen, onClose, setScreen}) => {
  const dispatch = useDispatch();
  const gameResults = useSelector(selectGameResults);
  const hexes = useSelector(selectHexes);
  const units = useSelector(selectUnits);
  const specialBuildings = useSelector(selectSpecialBuildings);
  const gridConfig = useSelector(selectGridConfigState);
  const hideModalsExceptGameEnd = useSelector(selectHideModalsExceptGameEnd);
  const isUnitAutoSelectionEnabled = useSelector(selectIsUnitAutoSelectionEnabled);
  const auth = useSelector((state) => state.private.auth);
  const lobby = useSelector((state) => state.private.lobby);
  const gameType = useSelector(selectGameType);
  const players = useSelector(selectPlayers);
  const fileInputRef = useRef(null);
  const isCreator = auth.id && auth.id === lobby.creatorId;
  const isAdmin = auth.isAdmin || auth.isSuperAdmin;

  // Подтверждение показывается ВМЕСТО меню — та же панель, то же оформление
  const [isLeaveConfirmOpen, setIsLeaveConfirmOpen] = useState(false);

  // Пока окна скрыты, их флаги в сторе копятся: гасит флаг только сама модалка
  // при закрытии, а смонтирована она не была. Поэтому при ВЫКЛЮЧЕНИИ настройки
  // игрок получил бы разом бой десятиминутной давности, чужой захват города и
  // «конец раунда» посреди раунда — гасим накопленное здесь же.
  // resetModalVisibility для этого не годится: он сбрасывает и showGameEndModal,
  // то есть закрыл бы итоги уже доигранной партии
  const handleHideModalsToggle = () => {
    if (hideModalsExceptGameEnd) {
      dispatch(hideCombatResultsModal());
      dispatch(hideCityCaptureModal());
      dispatch(hideRoundEndModal());
    }
    dispatch(toggleHideModalsExceptGameEnd());
  };

  // Автовыбор отрядов на этапах войны (change unit-auto-selection): выбор
  // запоминается в браузере и действует во всех партиях
  const handleUnitAutoSelectionToggle = () => {
    writeUnitAutoSelectionEnabled(!isUnitAutoSelectionEnabled);
    dispatch(setUnitAutoSelectionEnabled(!isUnitAutoSelectionEnabled));
  };

  // Закрытое меню компонент не размонтирует (ниже просто return null), поэтому
  // вопрос надо гасить руками: иначе следующее открытие меню показало бы не
  // меню, а зависший вопрос
  useEffect(() => {
    if (!isOpen) {
      setIsLeaveConfirmOpen(false);
    }
  }, [isOpen]);

  // Зритель матч не покидает — он в нем не участвует: его нет в составе партии,
  // сервер по LEAVE_MATCH ему все равно ничего не пометит, а кнопка сбивала бы
  // с толку. Состав берется из самой партии, а не из состояния лобби: он есть у
  // любого клиента в матче и не зависит от свежести LOBBY_UPDATE
  const isMatchParticipant = (players || []).some((player) => player.id === auth.id);

  // Покинуть можно только СЕТЕВОЙ матч: в локальной игре и в реплее лобби на
  // сервере нет, уведомлять некого — там уже есть «В главное меню»
  const canLeaveMatch = gameType === GAME_TYPES.ONLINE
    && Boolean(lobby.lobbyId)
    && !gameResults
    && isMatchParticipant;

  if (!isOpen) {
    return null;
  }

  const handleSaveState = () => {
    const stateToSave = {
      hexes,
      units,
      specialBuildings,
      gridConfig,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };

    const jsonString = JSON.stringify(stateToSave, null, 2);
    const blob = new Blob([jsonString], {type: 'application/json'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `hex-game-state-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    alert('Состояние сохранено в файл!');
  };

  const handleLoadState = () => {
    fileInputRef.current.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const loadedState = JSON.parse(e.target.result);
        if (!loadedState.hexes) {
          throw new Error('Некорректный формат файла: отсутствует поле hexes');
        }
        dispatch(loadState(loadedState));
        onClose();
      } catch (error) {
        console.error('Ошибка при загрузке состояния:', error);
        alert(`Ошибка при загрузке состояния: ${error.message}`);
      }
    };
    reader.onerror = () => {
      alert('Ошибка при чтении файла');
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const handleClearAll = () => {
    if (window.confirm('Вы уверены, что хотите очистить все поле? Это действие нельзя отменить.')) {
      dispatch(clearAllHexes());
    }
  };

  const onMainMenuClick = () => {
    closeNetworkSocket();
    dispatch({...resetState(), _fromServer: true});
    setScreen(SCREENS.MENU);
  }

  // Уход из идущего матча: сервер помечает игрока покинувшим (лобби живет
  // дальше, его отряды и города остаются на поле), после чего клиент уходит в
  // меню тем же путем, что и по кнопке «В главное меню»
  const onLeaveMatchConfirm = () => {
    const ws = getNetworkSocket();
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({type: 'LEAVE_MATCH', payload: {lobbyId: lobby.lobbyId}}));
    }
    setIsLeaveConfirmOpen(false);
    onMainMenuClick();
  };

  const onEndGameClick = () => {
    if (!window.confirm('Вы уверены, что хотите прервать текущую игру? Это действие нельзя отменить.')) {
      return;
    }
    const ws = getNetworkSocket();
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({type: 'END_GAME', payload: {lobbyId: lobby.lobbyId}}));
    }
  }

  if (isLeaveConfirmOpen) {
    return (
      <div
        className="modal-window_overlay"
        onClick={(e) => {
          e.stopPropagation();
          setIsLeaveConfirmOpen(false);
        }}
      >
        <div className="in-game-menu" onClick={(e) => e.stopPropagation()}>
          <p className="in-game-menu-confirm-text">
            Покинуть матч?
            <span className="in-game-menu-confirm-note">
              Вы вернетесь в главное меню. Ваши города и отряды останутся на поле,
              а матч продолжится без вас. Обычный матч, который покинули все
              игроки, завершается без наград.
            </span>
          </p>
          <div className="menu-buttons">
            <PageButton
              className="menu-button_leave-match"
              onClick={onLeaveMatchConfirm}
              pageId={PAGE_ID}
              buttonId="leave_match_confirm"
            >Покинуть матч</PageButton>
            <PageButton
              onClick={() => setIsLeaveConfirmOpen(false)}
              pageId={PAGE_ID}
              buttonId="leave_match_cancel"
            >Отмена</PageButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="modal-window_overlay"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div className="in-game-menu" onClick={(e) => e.stopPropagation()}>
        <div className="menu-buttons">
          {isCreator && isAdmin && !gameResults && (
            <PageButton
              className="menu-button_end-game"
              onClick={onEndGameClick}
              pageId={PAGE_ID}
              buttonId="end_game"
            >Завершить игру</PageButton>
          )}
          <PageButton
            onClick={onMainMenuClick}
            pageId={PAGE_ID}
            buttonId="main_menu"
          >В главное меню</PageButton>
          {canLeaveMatch && (
            <PageButton
              className="menu-button_leave-match"
              onClick={() => setIsLeaveConfirmOpen(true)}
              pageId={PAGE_ID}
              buttonId="leave_match"
            >Покинуть матч</PageButton>
          )}
          {/* Правила целиком — с обзора игры; раздел текущей фазы открывает
              кнопка «i» в футере */}
          <PageButton
            onClick={() => {
              onClose();
              dispatch(showConstructionPhaseInfoModal(RULES_SECTION_IDS.OVERVIEW));
            }}
            pageId={PAGE_ID}
            buttonId="rules"
            className="menu-button_rules"
            data-tutorial-select={'in_game_menu_rules'}
          >
            <img
              className="menu-button_rules-icon"
              src="/img/icons/rules.png"
              alt=""
            />
            Правила
          </PageButton>
          <FullscreenToggleButton pageId={PAGE_ID}/>
          <PageButton
            onClick={onClose}
            pageId={PAGE_ID}
            buttonId="hide"
          >Скрыть</PageButton>

          {isEnvConfEnabled(ENV_CONFIG_VARIABLES.REACT_APP_LOCAL_DEV_MODE) ?
            <>
              <PageButton
                className="menu-button-dev"
                onClick={handleSaveState}
              >Сохранить поле</PageButton>
              <PageButton
                className="menu-button-dev"
                onClick={handleLoadState}
              >Загрузить поле</PageButton>
              <PageButton
                className="menu-button-dev"
                onClick={handleClearAll}
              >Очистить поле</PageButton>
            </> : null
          }

          <label className="in-game-menu-checkbox">
            <input
              type="checkbox"
              checked={hideModalsExceptGameEnd}
              onChange={handleHideModalsToggle}
            />
            <span>Не отображать всплывающие окна</span>
          </label>

          <label className="in-game-menu-checkbox">
            <input
              type="checkbox"
              checked={isUnitAutoSelectionEnabled}
              onChange={handleUnitAutoSelectionToggle}
            />
            <span>Автовыбор отрядов</span>
          </label>
        </div>

        <VolumeSlider/>
        <MusicVolumeSlider/>

        <input
          type="file"
          ref={fileInputRef}
          style={{display: 'none'}}
          accept=".json,application/json"
          onChange={handleFileSelect}
        />
      </div>
    </div>
  );
};

export default InGameMenu;
